import React, { useState } from "react";

import { HubAccessLevel } from "@gamedev-web/one-gaming-crm";

import { Modal } from "../../../../components/Modal/Modal";
import { useStrings } from "../../../../localization/localizationHelpers";
import type { AdminContact } from "../../../../models/adminPage";
import { InvitationForm } from "../InviteContactModal";
import { InvitationRole } from "../InviteContactModal/InvitationRole";
import * as roleStyles from "../InviteContactModal/InviteContactModal.module.css";

type MemberDetailsModalProps = Readonly<{
  canTransferPrimarySignatory: boolean;
  contact: AdminContact | null;
  readOnly: boolean;
  showPrimarySignatorySection: boolean;
  onClose: () => void;
  onChangeRole: (contactId: string, accessLevel: HubAccessLevel) => void;
  onRemove: (contact: AdminContact) => void;
  onTransferPrimarySignatory: () => void;
}>;

export const MemberDetailsModal = ({
  canTransferPrimarySignatory,
  contact,
  readOnly,
  showPrimarySignatorySection,
  onClose,
  onChangeRole,
  onRemove,
  onTransferPrimarySignatory,
}: MemberDetailsModalProps) => {
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [
    closeLabel,
    changeLabel,
    businessContactLabel,
    businessContactLegalDescription,
    businessContactAssignmentDescription,
    noHubAccessLabel,
    cancelLabel,
    saveLabel,
    removeLabel,
    primarySignatoryLabel,
    primarySignatoryDescription,
    transferLabel,
  ] = useStrings(
    "partnerAdminConfirmationCloseLabel",
    "partnerMemberChangeRoleLabel",
    "partnerAdminBusinessContactLabel",
    "partnerAdminBusinessContactLegalDescription",
    "partnerAdminBusinessContactAssignmentDescription",
    "partnerAdminNotHubEnabledLabel",
    "partnerAdminInviteCancelLabel",
    "partnerSettingsSaveLabel",
    "partnerAdminRemoveMemberLabel",
    "partnerAdminPrimarySignatoryLabel",
    "partnerAdminPrimarySignatoryDescription",
    "partnerAdminTransferPrimarySignatoryLabel"
  );

  if (!contact) {
    return null;
  }

  const name = `${contact.firstName} ${contact.lastName}`.trim() || contact.email;
  const canEdit = !readOnly && contact.isAccountContact !== false && !contact.isCurrentUser;

  return (
    <Modal
      isOpen
      onClose={onClose}
      ariaLabelledBy="partner-member-details-title"
      closeOnOutsideClick={false}
    >
      <InvitationForm
        resetKey={`${contact.contactId}-${isChangingRole}`}
        initialInvitation={{
          firstName: contact.firstName,
          lastName: contact.lastName,
          jobTitle: contact.jobTitle,
          email: contact.email,
          accessLevel: contact.hubRole ?? HubAccessLevel.User,
        }}
        title={name}
        titleId="partner-member-details-title"
        description={contact.email}
        roleContent={
          !contact.isHubEnabled ? (
            <div
              className={`${roleStyles.roleOption} ${roleStyles.roleOptionSelected} ${roleStyles.assignedRole}`}
            >
              <InvitationRole
                option={{
                  label: businessContactLabel,
                  permissionLabels: [
                    businessContactLegalDescription,
                    businessContactAssignmentDescription,
                  ],
                }}
                hubAccessLabel={noHubAccessLabel}
                hasHubAccess={false}
              />
            </div>
          ) : undefined
        }
        submitLabel={saveLabel}
        cancelLabel={readOnly ? closeLabel : cancelLabel}
        closeLabel={closeLabel}
        areContactFieldsReadOnly
        roleMode={!contact.isHubEnabled ? "hidden" : isChangingRole ? "select" : "assigned"}
        hideOrganizationEmailNotice
        hideSubmit={!canEdit || !isChangingRole}
        hideCancel
        assignedRoleActionLabel={
          canEdit && contact.isHubEnabled && !isChangingRole ? changeLabel : undefined
        }
        dangerActionLabel={canEdit ? removeLabel : undefined}
        onCancel={onClose}
        onSecondaryAction={() => setIsChangingRole(true)}
        onDangerAction={() => onRemove(contact)}
        onSubmit={async invitation => {
          onChangeRole(contact.contactId, invitation.accessLevel);
          onClose();
        }}
      >
        {contact.isPrimarySignatory && showPrimarySignatorySection && (
          <section className={roleStyles.section}>
            <div className={roleStyles.header}>
              <div className={roleStyles.roleDetails}>
                <strong className={roleStyles.roleTitle}>{primarySignatoryLabel}</strong>
              </div>
              {canTransferPrimarySignatory && (
                <button
                  type="button"
                  className={roleStyles.roleAction}
                  onClick={onTransferPrimarySignatory}
                >
                  {transferLabel}
                </button>
              )}
            </div>
            <p className={roleStyles.description}>{primarySignatoryDescription}</p>
          </section>
        )}
      </InvitationForm>
    </Modal>
  );
};
